/**
 * 問題の描画用の木（docs/spec/problem-format.md §5.2）。
 * ビルド時（Node）に parseProblem が作り、画面はこの形だけを読む（mdast は渡さない）。
 * JSON にできる値だけで組む（virtual:problems がそのまま JSON で埋め込む）。
 * Inline / Block / HintBlock / ThinkStep はレッスンと共通（src/lib/lessons/types.ts）。
 *
 * 問題に固有のノード: step（ThinkStep）、hint（HintBlock）、essential（EssentialBlock）、exercise（ExerciseNode）。
 */
import type { Block, HintBlock, Inline, ThinkStep } from '../lessons/types.js';

export type { Block, HintBlock, Inline, ThinkStep };

/**
 * 手法の語彙（schemas/problem.schema.json の $defs/technique = card-schema.md §3.5）。
 * src/lib/cards/types.ts の Technique と同じ列（cards は Node から直接読めない import の書き方なので、
 * ビルド時のコードから参照しないようにここに写す。一致はテストで確かめる）。
 */
export type Technique =
  | 'single-loop'
  | 'double-loop-search'
  | 'triple-loop-search'
  | 'digit-while'
  | 'array-count'
  | 'dict'
  | 'set'
  | 'stack'
  | 'queue'
  | 'cumulative-sum'
  | 'sort-and-binary-search'
  | 'sort-and-greedy'
  | 'two-pointers'
  | 'bit-full-search'
  | 'bfs'
  | 'dfs'
  | 'dp';

export type Syntax =
  | 'read-line'
  | 'split-map'
  | 'int-range'
  | 'arithmetic'
  | 'if-else'
  | 'logical-ops'
  | 'for-in'
  | 'stride'
  | 'while'
  | 'array'
  | 'array-2d'
  | 'sort'
  | 'string'
  | 'character'
  | 'dict'
  | 'set'
  | 'tuple'
  | 'optional'
  | 'function'
  | 'closure'
  | 'joined-output';

export interface ProblemCase {
  name?: string;
  in: string;
  out: string;
  sample?: boolean;
}

export interface StressSpec {
  gen: string;
}

export interface ProblemSource {
  contest: string;
  problem: string;
  url: string;
  difficulty?: number;
}

export interface ProblemLessonRef {
  id: string;
  /** 見出しの文字列そのまま（比較は sectionSlug にしてから。§6.1）。 */
  section: string;
}

/** 類題（problem-format.md §2.5）。 */
export interface ExerciseMeta {
  /** `<contest>-<problem の小文字>`（source と一致）。書きかけのキーは `<problemId>/<id>`。 */
  id: string;
  title: string;
  source: ProblemSource;
  /** 自分の言葉の要約（プレーンテキスト。改行を含んでよい）。 */
  summary: string;
  /** 省略時は例題の techniques。 */
  techniques?: Technique[];
  starter: string;
  starter_compiles?: boolean;
  cases: ProblemCase[];
  solution: string;
  /** 段階ヒント 3 段（プレーンテキスト）。 */
  hints: [string, string, string];
  stress?: StressSpec;
  /** 省略時は例題の time_limit_sec。 */
  time_limit_sec?: number;
}

export interface ProblemMeta {
  schemaVersion: 1;
  id: string;
  title: string;
  source: ProblemSource;
  order: number;
  techniques: Technique[];
  syntax: Syntax[];
  lessons: ProblemLessonRef[];
  /** 省略時は []。 */
  memos: string[];
  cases: ProblemCase[];
  starter: string;
  starter_compiles?: boolean;
  solution: string;
  stress?: StressSpec;
  time_limit_sec: number;
  /** 例題の段階ヒント 3 段（プレーンテキスト）。 */
  hints: [string, string, string];
  exercises: ExerciseMeta[];
  verified: { swift: '6.2.0'; date: string };
  draft?: boolean;
}

export interface ProblemStatement {
  /** 先頭の段落 1〜3 個。 */
  summary: Block[];
  /** ### 制約 の中身（箇条書き 1 つ）。 */
  constraints: Block[];
  /** ### 入力 の中身。 */
  input: Block[];
  /** ### 出力 の中身。 */
  output: Block[];
}

/**
 * 要点（` ```text {essential lesson="<lessonId>#<節>"} `。problem-format.md §4）。「## 解説」に置く。
 * 1〜2 文のプレーンテキストと、任意で立ち返るレッスンの節へのリンク。
 */
export interface EssentialBlock {
  type: 'essential';
  /** 1 始まりの出現順。 */
  index: number;
  /** プレーンテキスト（Markdown として解かない）。末尾の改行は落とす。 */
  text: string;
  /**
   * 立ち返るレッスンの節。section は slug（sectionSlug 済み）。
   * href は `#/lesson/<id>#<slug>`（formatRoute の形。problemLinkTarget を通る）。
   */
  lesson?: { id: string; section: string; href: string };
  line: number;
}

/** 「## 解説」に現れるブロック（レッスンと共通の Block に要点を足したもの）。 */
export type ExplanationBlock = Block | EssentialBlock;

/** 類題 1 問（「## 類題」に front matter の exercises から並べる）。 */
export interface ExerciseNode {
  type: 'exercise';
  /** 1 始まり。 */
  index: number;
  /** meta.exercises[index - 1].id。 */
  id: string;
  meta: ExerciseMeta;
  hints: [HintBlock, HintBlock, HintBlock];
}

export interface ProblemDoc {
  meta: ProblemMeta;
  /** ## 問題（画面はこの直後にエディタ・実行と採点・ヒントを出す）。 */
  statement: ProblemStatement;
  /** 例題のヒント（front matter の hints から。line は front matter の行）。 */
  hints: [HintBlock, HintBlock, HintBlock];
  /** ## まずこう考える */
  think: ThinkStep[];
  /** ## 解説（### は heading ブロック。`### データと前提` を必ず含む）。 */
  explanation: ExplanationBlock[];
  /** ## 一般化 */
  generalization: Block[];
  /** ## 類題 の本文（段落 0〜2 個）。 */
  exercisesIntro: Block[];
  /** 類題（front matter の exercises の順）。 */
  exercises: ExerciseNode[];
  proseChars: number;
}

export type ProblemIssueCode =
  | 'PF01'
  | 'PF02'
  | 'PF03'
  | 'PF10'
  | 'PF11'
  | 'PF12'
  | 'PF13'
  | 'PF14'
  | 'PF15'
  | 'PF20'
  | 'PF21'
  | 'PF22'
  | 'PF30'
  | 'PF40'
  | 'PF41'
  | 'PF50'
  | 'PF51'
  | 'PF52'
  | 'PF53';

export interface ProblemIssue {
  code: ProblemIssueCode;
  line: number;
  message: string;
}

export type ParseProblemResult =
  | { ok: true; doc: ProblemDoc; warnings: ProblemIssue[] }
  | { ok: false; errors: ProblemIssue[]; warnings: ProblemIssue[] };

/** 本文の ## 節（§1。この順で各ちょうど 1 回。見出しは完全一致）。 */
export const PROBLEM_SECTIONS = ['問題', 'まずこう考える', '解説', '一般化', '類題'] as const;

export type ProblemSectionTitle = (typeof PROBLEM_SECTIONS)[number];

/** ## 問題 の ### 小節（この順で必須）。 */
export const STATEMENT_SUBSECTIONS = ['制約', '入力', '出力'] as const;

/** 「## 解説」に必ず 1 回置く小見出し。 */
export const PREMISES_HEADING = 'データと前提';

/** 「まずこう考える」で計算量の見積もりを書くステップの見出し。 */
export const BUDGET_STEP_LABEL = '計算量の予算';

/** 書きかけのキー（§8）。例題 `<problemId>`、類題 `<problemId>/<exerciseId>`。 */
export const PROBLEM_DRAFT_KEY_PATTERN = /^abc[0-9]{3,4}-[a-g](?:\/abc[0-9]{3,4}-[a-g])?$/;
